import React, { useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Mail, MapPin, Phone } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

const initial = { name: "", email: "", subject: "", message: "" };

export const Contact = () => {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.message) {
      toast.error("Please fill in all fields");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/contact`, form);
      toast.success("Message sent", { description: "We'll reply within 1-2 days." });
      setForm(initial);
    } catch {
      toast.error("Could not send. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-20 md:py-28 bg-primary text-primary-foreground">
      <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-2 gap-14">
        <div>
          <div className="font-mono-ui text-xs uppercase tracking-widest text-accent mb-3">
            — 05 / Say hello
          </div>
          <h2 className="font-display text-4xl md:text-6xl font-semibold tracking-tight leading-tight">
            Questions?<br />Come find us.
          </h2>
          <p className="mt-6 text-primary-foreground/70 max-w-md leading-relaxed">
            We answer every email. Suppliers, chefs, curious neighbors — all welcome.
          </p>

          <div className="mt-10 space-y-5">
            {[
              { icon: Mail, label: "hello@villagefarmfood.com" },
              { icon: Phone, label: "+91 93973 63140" },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-accent flex items-center justify-center shrink-0">
                  <item.icon className="w-4 h-4 text-accent-foreground" />
                </div>
                <span className="text-primary-foreground/90">{item.label}</span>
              </div>
            ))}
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-accent flex items-center justify-center shrink-0">
                <MapPin className="w-4 h-4 text-accent-foreground" />
              </div>
              <address
                data-testid="contact-address"
                className="text-primary-foreground/90 not-italic leading-relaxed"
              >
                <div className="font-display text-lg text-primary-foreground">Village Farm Food</div>
                <div>Village Malkash, Post Bhadi</div>
                <div>Tehsil Bhadra, District Hanuman Garh</div>
                <div>Rajasthan &mdash; 335501</div>
              </address>
            </div>
          </div>
        </div>

        <form
          onSubmit={submit}
          data-testid="contact-form"
          className="bg-background text-foreground rounded-2xl p-6 md:p-8 space-y-5 shadow-2xl"
        >
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-widest font-mono-ui">Name</Label>
              <Input
                data-testid="contact-name"
                value={form.name}
                onChange={(e) => update("name", e.target.value)}
                className="mt-2 h-11"
                required
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest font-mono-ui">Email</Label>
              <Input
                data-testid="contact-email"
                type="email"
                value={form.email}
                onChange={(e) => update("email", e.target.value)}
                className="mt-2 h-11"
                required
              />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest font-mono-ui">Subject</Label>
            <Input
              data-testid="contact-subject"
              value={form.subject}
              onChange={(e) => update("subject", e.target.value)}
              className="mt-2 h-11"
              required
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest font-mono-ui">Message</Label>
            <Textarea
              data-testid="contact-message"
              value={form.message}
              onChange={(e) => update("message", e.target.value)}
              className="mt-2 min-h-[140px]"
              required
            />
          </div>
          <Button
            type="submit"
            disabled={submitting}
            data-testid="contact-submit"
            className="w-full h-12 rounded-full bg-primary text-primary-foreground hover:bg-accent hover:text-accent-foreground text-sm font-semibold"
          >
            {submitting ? "Sending..." : "Send message"}
          </Button>
        </form>
      </div>
    </section>
  );
};
